import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/hooks/use-toast";
import {
  PaymentPage,
  PaymentPageFormData,
} from "@/pages/payment-pages/types/payment-page";
import { useCreatePaymentPage } from "../hooks/use-create-payment-page";
import { useUpdatePaymentPage } from "../hooks/use-update-payment-page";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import OfferingCombobox from "./offering-combobox";

export const paymentPageSchema = z.object({
  slug: z.string().min(1, "Slug is required"),
  region: z.string().min(1, "Region is required"),
  region_id: z.string().nullable().optional(),
  page_id: z.string().min(1, "Page id is required"),
  page_title: z.string().min(1, "Page title is required"),
  fallback_amount: z.number().nullable().optional(),
  currency: z.string().min(1, "Currency is required"),
  bundle_standard_id: z.string().nullable().optional(),
  redirect_link: z.string().nullable().optional(),
  login_redirect_url: z.string().nullable().optional(),
  is_auth_enabled: z.boolean(),
  description: z.string().optional(),
  plan_details: z.string().optional(),
  tnc: z.string().optional(),
  how_it_works: z.string().optional(),
  add_on: z
    .object({
      label: z.string().optional(),
      description: z.string().optional(),
      target_standard_id: z.string().optional(),
    })
    .optional(),
  layout_variant: z.enum(["classic", "v2"]).optional(),
  status: z.enum(["ACTIVE", "INACTIVE"]),
});

interface PaymentPageFormProps {
  initialData?: PaymentPage | null;
  onClose: () => void;
  editMode?: boolean;
  /**
   * Show the payment page without letting it be changed: fields disabled, the
   * footer reduced to Close. For viewers with View but not Edit on Payment
   * Pages — the drawer still opens, it just cannot save.
   */
  readOnly?: boolean;
}

export default function PaymentPageForm({
  initialData,
  onClose,
  editMode,
  readOnly = false,
}: PaymentPageFormProps) {
  const form = useForm<PaymentPageFormData>({
    resolver: zodResolver(paymentPageSchema),
    defaultValues: {
      slug: initialData?.slug || "",
      region: initialData?.region || "IN",
      page_id: initialData?.page_id || "",
      page_title: initialData?.page_title || "",
      fallback_amount: initialData?.fallback_amount ?? null,
      currency: initialData?.currency || "INR",
      bundle_standard_id: initialData?.bundle_standard_id ?? null,
      redirect_link: initialData?.redirect_link ?? null,
      login_redirect_url: initialData?.login_redirect_url ?? null,
      is_auth_enabled: initialData?.is_auth_enabled ?? false,
      description: initialData?.description
        ? JSON.stringify(initialData.description, null, 2)
        : "",
      plan_details: initialData?.plan_details
        ? JSON.stringify(initialData.plan_details, null, 2)
        : "",
      tnc: initialData?.tnc ? JSON.stringify(initialData.tnc, null, 2) : "",
      how_it_works: initialData?.how_it_works
        ? JSON.stringify(initialData.how_it_works, null, 2)
        : "",
      add_on: {
        label: initialData?.add_on?.label ?? "",
        description: initialData?.add_on?.description ?? "",
        target_standard_id: initialData?.add_on?.target_standard_id ?? "",
      },
      layout_variant:
        (initialData?.layout_variant as "classic" | "v2") || "classic",
      status: initialData?.status || "INACTIVE",
    },
  });

  const createMutation = useCreatePaymentPage();
  const updateMutation = useUpdatePaymentPage();
  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const onSubmit = async (data: PaymentPageFormData) => {
    try {
      if (editMode && initialData?.id) {
        await updateMutation.mutateAsync({ id: initialData.id, data });
      } else {
        await createMutation.mutateAsync(data);
      }
      toast({
        title: "Success",
        description: `Payment page ${editMode ? "updated" : "created"} successfully`,
      });
      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to save payment page",
        variant: "destructive",
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 p-6">
        <fieldset disabled={readOnly} className="space-y-4 min-w-0">
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="slug"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Slug</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    onChange={(e) =>
                      field.onChange(e.target.value.toLowerCase())
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="region"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Region</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="page_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Page ID</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="page_title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Page Title</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="fallback_amount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Fallback Amount</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    value={field.value ?? ""}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value ? Number(e.target.value) : null,
                      )
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="currency"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Currency</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="bundle_standard_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Bundle Standard ID</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value ?? ""} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="redirect_link"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Redirect Link</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value ?? ""} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="login_redirect_url"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Login Redirect URL</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value ?? ""} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="is_auth_enabled"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between border rounded-md p-3">
              <FormLabel>Require Auth</FormLabel>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="INACTIVE">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="layout_variant"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Page Design</FormLabel>
                <Select
                  value={field.value ?? "classic"}
                  onValueChange={field.onChange}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="classic">Classic (default)</SelectItem>
                    <SelectItem value="v2">New design</SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description JSON</FormLabel>
              <FormControl>
                <Textarea rows={4} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="plan_details"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Plan Details JSON</FormLabel>
              <FormControl>
                <Textarea rows={4} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="tnc"
          render={({ field }) => (
            <FormItem>
              <FormLabel>TnC JSON</FormLabel>
              <FormControl>
                <Textarea rows={4} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="how_it_works"
          render={({ field }) => (
            <FormItem>
              <FormLabel>How It Works JSON</FormLabel>
              <FormControl>
                <Textarea rows={4} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="border rounded-md p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Checkout add-on (optional)</p>
              <p className="text-xs text-gray-500">
                One toggle on the payment page that retargets the whole order
                to the selected offering (e.g. upgrade to the DIVA package).
              </p>
            </div>
            {form.watch("add_on.target_standard_id") ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() =>
                  form.setValue("add_on", {
                    label: "",
                    description: "",
                    target_standard_id: "",
                  })
                }
              >
                Clear
              </Button>
            ) : null}
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Target offering</p>
            <OfferingCombobox
              value={form.watch("add_on.target_standard_id") || null}
              onSelect={(b) =>
                form.setValue("add_on.target_standard_id", b.standard_id, {
                  shouldDirty: true,
                })
              }
            />
          </div>
          <FormField
            control={form.control}
            name="add_on.label"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Add-on label</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Add the DIVA diagnostic test"
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="add_on.description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Add-on description</FormLabel>
                <FormControl>
                  <Textarea rows={2} {...field} value={field.value ?? ""} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        </fieldset>

        <div className="flex justify-end gap-2">
          <Button variant="outline" type="button" onClick={onClose}>
            {readOnly ? "Close" : "Cancel"}
          </Button>
          {!readOnly && (
            <Button type="submit" disabled={isSubmitting}>
              {editMode ? "Update" : "Create"}
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
}
